import { isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, OnInit, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription, fromEvent } from 'rxjs';
import { Header } from 'src/app/shared/content/content';
import Swal from 'sweetalert2';
import { SimulationSession } from '../../models/simulation.model';
import { DataUser, RolesUser } from '../../models/users.model';
import { LocalStorageService } from '../../services/local-storage.service';
import { SimulationService } from '../../services/simulation.service';
import { TagManagerService } from '../../services/tag-manager.service';
import { UserAgentService } from '../../services/user-agent.service';
import { UsersService } from '../../services/users.service';
import { InactivityService } from '../../services/inactivity.service';
import { SessionService } from '../../services/session.service';

const PENDING_SELECTION_KEY = 'ROD-pending-client-selection';
const VE_BASE_USER_KEY = 'ROD-ve-base-user';
const VE_BASE_USER_DATA_KEY = 'ROD-ve-base-userData';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit {
	openNav: boolean = false;
	user!: DataUser | null;
	isMobile: boolean = false;
	dealerInfos: any = '';
	lpDealerId: any = '';
	lpDealerData: any = '';
	session: SimulationSession[] = [];
	resizeObservable!: Observable<Event>;
	resizeSubscription!: Subscription;
	routerSubscription!: Subscription;
	isDealer: boolean = false;
	isSeller: boolean = false;
	isVE: boolean = false;
	isKA: boolean = false;
	fixedHeader: boolean = true;
	isConsultant: boolean = false;
	textHeader = Header;
	mobilizesLogo: boolean = false;
	isLoading: boolean = false;
	hasAssociatedClients: boolean = false;

	subRouter: string = '';
	loggedUser: any;

	shouldRender = false;

	constructor(
		private router: Router,
		@Inject(DOCUMENT) private document: Document,
		private userAgent: UserAgentService,
		private localStorage: LocalStorageService,
		@Inject(PLATFORM_ID) private platformId: Object,
		private tagManager: TagManagerService,
		private simulationService: SimulationService,
		private usersService: UsersService,
		private inactivityService: InactivityService,
		private sessionService: SessionService
	) {
		this.isLoading = true;

		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				const url = event.url;
				this.mobilizesLogo = [
					'/renta-de-carros-mensual/rapido/dados-da-empresa',
					'/renta-de-carros-mensual/rapido/dados-pessoais',
					'/renta-de-carros-mensual/rapido/finalizar',
					'/renta-de-carros-mensual/rapido/sucesso',
					'/area-logada',
					'/area-logada/perfil',
					'/area-logada/pedidos',
					'/area-logada/contratos',
					'/area-logada/dados-de-pagamento',
					'/area-logada/servicos',
				].includes(url);

				this.verifyUser();
				this.session = this.simulationService.getSimulationSession();
				this.fixedHeader = !url.startsWith('/simulador/simule-y-rente');
			}
		});

		UsersService.loggedOut.subscribe(() => {
			this.user = null;
		});

		UsersService.isLPDEALER.subscribe(() => {
			this.verifyUser();
		});

		this.isMobile = userAgent.verifyMobile();
		this.dealerInfos = localStorage.getItem('ROD-logged.dealership');
		this.lpDealerId = localStorage.getItem('ROD-LP-DEALER-dealerid');
		this.lpDealerData = localStorage.getItem('ROD-LP-DEALER-dealer');
		this.session = simulationService.getSimulationSession();
	}

	ngOnInit(): void {
		if (!isPlatformBrowser(this.platformId)) return;

		this.verifyUser();

		if (isPlatformBrowser(this.platformId)) {
			this.resizeObservable = fromEvent(window, 'resize');
		}

		this.routerSubscription = this.router.events.subscribe(() => {
			if (this.openNav) this.toggleNav();
		});

		this.resizeSubscription = this.resizeObservable?.subscribe(() => {
			this.onResize();
			if (isPlatformBrowser(this.platformId) && window.innerWidth >= 1100 && this.openNav) {
				this.toggleNav();
			}
		});

		this.simulationService.session$.subscribe((session: any) => {
			this.session = session;
		});
	}

	toggleNav() {
		if (isPlatformBrowser(this.platformId)) {
			this.openNav = !this.openNav;
			this.openNav
				? this.document.body.classList.add('modal-open')
				: this.document.body.classList.remove('modal-open');
		}
	}

	verifyUser() {
		const user = this.localStorage.getItem('ROD-logged.user');
		if (user !== undefined && user !== null) {
			this.user = JSON.parse(user);
			this.getAuthenticatedUserFromBackend();
			this.getUser();
		} else {
			this.isConsultant = false;
			this.isDealer = false;
			this.isSeller = false;
			this.isVE = false;
			this.isKA = false;
			this.isLoading = false;
		}

		if (this.isDealer) {
			this.dealerInfos = this.localStorage.getItem('ROD-logged.dealership');
			if (this.dealerInfos !== undefined && this.dealerInfos !== null && typeof this.dealerInfos === 'string') {
				this.dealerInfos = JSON.parse(this.dealerInfos);
			}
		}

		this.lpDealerId = this.localStorage.getItem('ROD-LP-DEALER-dealerid');
		this.lpDealerData = this.localStorage.getItem('ROD-LP-DEALER-dealer');
		if (this.lpDealerData !== undefined && this.lpDealerData !== null && typeof this.lpDealerData === 'string') {
			this.lpDealerData = JSON.parse(this.lpDealerData);
		}
	}

	/**
	 * Obtiene el rol del usuario autenticado desde el backend.
	 * El backend lee el rol del token JWT (que no puede ser modificado sin invalidar la firma).
	 * Esto previene que un usuario modifique el rol en la respuesta del login.
	 */
	private getAuthenticatedUserFromBackend(): void {
		this.usersService.getAuthenticatedUser().subscribe({
			next: (result: any) => {
				if (result?.data) {
					const validatedRole = result.data.role;
					this.isConsultant = validatedRole === RolesUser.CONSULTANT;
					this.isSeller = validatedRole === RolesUser.SELLER;
					this.isVE = validatedRole === RolesUser.VE;
					this.isKA = validatedRole === RolesUser.KA;
					this.isDealer = !!result.data.dealershipId || !!this.user?.authContext?.dealershipId;

					if (this.user && this.user.authContext) {
						this.user.authContext.role = validatedRole;
						this.user.authContext.dealershipId = result.data.dealershipId || this.user.authContext.dealershipId;
						this.localStorage.setItem('ROD-logged.user', JSON.stringify(this.user));
					}

					this.applyOwnerRoleOverrides();
				}
			},
			error: (err: any) => {
				console.error('Error al validar el usuario autenticado: ', err);

				if (err?.status === 401 || err?.status === 403) {
					this.localStorage.clear();
					this.user = null;
					this.isDealer = false;
					this.isConsultant = false;
					this.isSeller = false;
					this.isVE = false;
					this.isKA

					const publicUrls = ['/conta/login', '/conta/esqueci-minha-senha'];
					if (!publicUrls.some(p => this.router.url.includes(p))) {
						this.router.navigate(['/conta/login']);
					}
				} else {
					// Error de red — no cerrar sesión, pero tampoco confiar en roles del localStorage
					this.isDealer = false;
					this.isConsultant = false;
					this.isSeller = false;
					this.isVE = false;
					this.isKA = false;
				}
			},
		});
	}

	private getUser(): void {
		this.usersService.getUser().subscribe({
			next: (result: any) => {
				this.loggedUser = result.data;
				const hasOwnedClients = (result?.data?.ownedClientIds?.length || 0) > 0;
				this.hasAssociatedClients = !!result?.data?.hasAccessToAllClients || hasOwnedClients;
				this.applyOwnerRoleOverrides();
				this.isLoading = false;
			},
			error: () => {
				this.hasAssociatedClients = false;
				this.applyOwnerRoleOverrides();
			},
		});
	}

	private applyOwnerRoleOverrides(): void {
		const ownerRole = this.localStorage.getItem('ROD-owner-role');
		const isVeOwnerView = this.localStorage.isOwnerView() && ownerRole === RolesUser.VE;

		if (!isVeOwnerView) {
			return;
		}

		this.isVE = true;
		this.isSeller = false;
		this.isConsultant = false;
		this.isKA = false;
		this.isDealer = false;

		if (!this.hasAssociatedClients) {
			const baseUserData = JSON.parse(this.localStorage.getItem(VE_BASE_USER_DATA_KEY) || '{}');
			const hasOwnedClients = (baseUserData?.ownedClientIds?.length || 0) > 0;
			this.hasAssociatedClients = !!baseUserData?.hasAccessToAllClients || hasOwnedClients;
		}

		if (!this.loggedUser) {
			this.loggedUser = JSON.parse(this.localStorage.getItem(VE_BASE_USER_DATA_KEY) || '{}');
		}
	}

	openClientsPortal(event: Event): void {
		event.preventDefault();

		if (!this.isVE || !this.hasAssociatedClients) {
			return;
		}

		const userData = this.loggedUser || JSON.parse(this.localStorage.getItem('ROD-logged.userData') || '{}');
		const currentUser = JSON.parse(this.localStorage.getItem('ROD-logged.user') || '{}');
		const ownedClientIds = userData?.ownedClientIds || [];
		const hasAccessToAllClients = !!userData?.hasAccessToAllClients;

		if (!hasAccessToAllClients && ownedClientIds.length === 0) {
			return;
		}

		if (currentUser?.token) {
			this.localStorage.setItem(VE_BASE_USER_KEY, JSON.stringify(currentUser));
		}

		if (userData && Object.keys(userData).length > 0) {
			this.localStorage.setItem(VE_BASE_USER_DATA_KEY, JSON.stringify(userData));
		}

		this.localStorage.setItem(
			PENDING_SELECTION_KEY,
			JSON.stringify({
				hasAccessToAllClients,
				ownedClientIds,
				role: RolesUser.VE,
				ownerToken: currentUser?.token,
			})
		);

		this.router.navigate(['/conta/seleccionar-cliente']);
	}

	goToMyProposals(event: Event): void {
		event.preventDefault();
		const ownerRole = this.localStorage.getItem('ROD-owner-role');

		if (this.localStorage.isOwnerView() && ownerRole === RolesUser.VE) {
			const baseUser = this.localStorage.getItem(VE_BASE_USER_KEY);
			const baseUserData = this.localStorage.getItem(VE_BASE_USER_DATA_KEY);

			if (baseUser) {
				this.localStorage.setItem('ROD-logged.user', baseUser);
			}

			if (baseUserData) {
				this.localStorage.setItem('ROD-logged.userData', baseUserData);
				this.loggedUser = JSON.parse(baseUserData);
			}

			this.localStorage.removeItem('ROD-owner-view');
			this.localStorage.removeItem('ROD-owner-role');
			this.localStorage.removeItem('ROD-owner-data');
			this.localStorage.removeItem(PENDING_SELECTION_KEY);
		}

		this.router.navigate(['/concessionarias/propuestas']);
	}

	get displayedUserName(): string {
		const ownerRole = this.localStorage.getItem('ROD-owner-role');
		const isVeOwnerView = this.localStorage.isOwnerView() && ownerRole === RolesUser.VE;

		if (isVeOwnerView) {
			const baseUserData = JSON.parse(this.localStorage.getItem(VE_BASE_USER_DATA_KEY) || '{}');
			return [baseUserData?.name, baseUserData?.secondname, baseUserData?.surname].filter(Boolean).join(' ');
		}

		return [this.loggedUser?.name, this.loggedUser?.secondname, this.loggedUser?.surname].filter(Boolean).join(' ');
	}

	logOut() {
		this.usersService.logOut().subscribe({
			next: () => {
				// FIX: mostrar Swal y limpiar la sesión solo después de confirmación
				void Swal.fire({
					title: 'Sesión cerrada',
					html: '<div>Cerraste sesión manualmente.</div>',
					icon: 'success',
					confirmButtonText: 'Ok',
					buttonsStyling: false,
					customClass: { confirmButton: 'btn btn-primary' }
				}).then(() => {
					this.clearSession();
				}).catch(() => {
					this.clearSession();
				});
			},
			error: () => {
				console.error('Error during logout');
				this.clearSession();
			},
		});
	}

	private clearSession(): void {
		this.inactivityService.stopWatching();
		this.sessionService.clearSessionHard('manual');
	}

	@HostListener('window:resize')
	onResize() {
		this.isMobile = this.userAgent.verifyMobile();
	}

	emitEventSimulation(title: string) {
		this.tagManager.setTagManager({
			event: 'gaEvent',
			eventCategory: 'simulacao',
			eventAction: 'clique',
			eventLabel: 'inicio',
		});
		this.emitEvent(title);
	}

	emitEvent(title: string) {
		this.tagManager.setTagManager({
			event: 'gaEvent',
			eventCategory: 'header',
			eventAction: 'clique',
			eventLabel: title,
		});
	}

	generateLinkVendaAssistida() {
		if (this.user?.authContext?.originAssistedSale) {
			const url = `${document.location.host}?vendaassistida=${this.user.authContext.originAssistedSale.toUpperCase()}`;
			this.copyToClipboard(url);
			Swal.fire({
				title: '¡Éxito!',
				text: 'Enlace copiado con éxito',
				icon: 'success',
				confirmButtonText: 'Ok',
				buttonsStyling: false,
				customClass: { confirmButton: 'btn btn-primary' },
			});
		} else {
			Swal.fire({
				title: '¡Atención!',
				text: this.isSeller
					? 'No identificamos el origen de la venta asistida de tu usuario'
					: 'No identificamos el origen de KA para tu usuario',
				icon: 'warning',
				confirmButtonText: 'Ok',
				buttonsStyling: false,
				customClass: { confirmButton: 'btn btn-primary' },
			});
		}
	}

	private copyToClipboard(text: string) {
		if (isPlatformBrowser(this.platformId)) {
			const textArea = document.createElement('textarea');
			document.body.appendChild(textArea);
			textArea.value = text;
			textArea.select();
			document.execCommand('copy');
			document.body.removeChild(textArea);
		}
	}
}